import * as React from 'react';
import { ReactNode } from 'react';
import '../css/card.sass';
import Hat, { HatType } from './Hat';
import Shoe from './Shoe';
import style2object from '../utils/style-object';
import shouldFlipBG from '../utils/should-flip-bg';
import StaticElementButton from './StaticElementButton';

interface CardProps {
  theme?: 'day' | 'night';
  children: ReactNode;
  hat?: HatType[];
  shoe?: ReactNode;
  style?: string;
  backgroundColor?: string;
  height?: string;
  width?: string;
  icon?: 'account' | 'billing' | 'hamburger';
  iconUrl?: string;
  selectable?: boolean;
  selected?: boolean;
  disabled?: boolean;
  onChange?: Function;
  selectableCardAriaLabel?: string;
  selectableCardId?: string;
  ariaLabelledByLabel?: string;
  isRoleButton?: boolean;
}

interface CardState {
  flipBg?: string;
  selected: boolean;
  hover: boolean;
}

export default function SelectableCard(props: CardProps) {
  const {
    theme = 'day',
    selectableCardAriaLabel = 'selectableCardName',
    selectableCardId = 'selectableCardId'
  } = props;
  const inputElParent = React.useRef(null);
  const [state, setState] = React.useState<CardState>({
    flipBg: '',
    selected: props.selected || false,
    hover: false
  });

  const handleKeyDown = (event: any) => {
    if (event.keyCode === 13 || event.keyCode === 32) {
      event.preventDefault();
      if (!props.disabled) {
        setState({ ...state, selected: !state.selected });
      }
    }
  };

  React.useEffect(() => {
    if (!state.flipBg && shouldFlipBG(inputElParent, theme)) {
      setState({
        ...state,
        flipBg: ' flipbg'
      });
    } else {
      setState({
        ...state,
        flipBg: ''
      });
    }
  }, []);

  React.useEffect(() => {
    if (props.onChange) {
      props.onChange(state.selected);
    }
  }, [state.selected]);

  const customStyle = props.style ? style2object(props.style) : {};
  if (props.backgroundColor) {
    customStyle.backgroundColor = props.backgroundColor;
  }
  if (props.height) {
    customStyle.height = props.height;
  }
  if (props.width) {
    customStyle.width = props.width;
  }
  let iconClassName: string | null = null;
  if (props.iconUrl) {
    iconClassName = 'custom';
  } else if (props.icon) {
    iconClassName = `${props.icon}`;
  }

  let style: string;
  if (state.selected) {
    style = 'selected';
  } else if (!state.selected && state.hover) {
    style = 'hover';
  } else {
    style = 'hover-out';
  }
  return (
    <>
      {props.hat &&
        props.hat.length > 0 &&
        props.hat[0] &&
        props.hat[0].message && <Hat hat={props.hat} theme={theme} />}
      <div className="selectable-card-container">
        <StaticElementButton
          name="selectableCard"
          id={selectableCardId}
          label={selectableCardAriaLabel}
          type="div"
          onClick={() => {
            if (!props.disabled) {
              setState({ ...state, selected: !state.selected });
            }
          }}
          isRoleButton={props.isRoleButton}
        >
          <div
            ref={inputElParent}
            className={`card${state.flipBg} ${theme}${
              props.disabled ? ' disabled' : ''
            }${state.selected ? ' selected-border' : ''}`}
            style={customStyle}
          >
            {iconClassName && (
              <div
                className={`card-icon ${iconClassName}`}
                style={
                  props.iconUrl
                    ? { backgroundImage: `url(${props.iconUrl})` }
                    : {}
                }
              />
            )}            
            {state.flipBg !== undefined && props.children}
          </div>
        </StaticElementButton>
        {props.selectable && (
              <>
                <input
                  type="checkbox"
                  checked={state.selected}
                  className={`selectable-icon check ${style}`}
                  onClick={() => {
                    setState({ ...state, selected: !state.selected });
                  }}
                  onMouseOver={() => {
                    setState({ ...state, hover: !state.hover });
                  }}
                  onMouseOut={() => {
                    setState({ ...state, hover: !state.hover });
                  }}
                  onBlur={() => {
                    setState({ ...state, hover: !state.hover });
                  }}
                  onFocus={() => {
                    setState({ ...state, hover: !state.hover });
                  }}
                  onKeyDown={(e)=>{
                    handleKeyDown(e)
                  }}
                  disabled={props.disabled}
                  aria-labelledby={props.ariaLabelledByLabel}                  
                />
                <div className="selectable-icon icon"></div>
              </>
            )}
      </div>
      {props.shoe && <Shoe>{props.shoe}</Shoe>}
    </>
  );
}
